import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, ApiError, type FeatureFlag } from "../lib/api.js";
import { useAuth } from "../lib/auth-context.js";

/**
 * Tela admin-only para inspecionar e alternar feature flags do backend.
 *
 * Fluxo: GET /api/feature-flags no boot → lista os flags → cada linha tem
 * um toggle que dispara PATCH /api/feature-flags/:key com { enabled: !cur }.
 * O toggle é OTIMISTA: a UI muda imediatamente ao clique; se o PATCH falhar
 * (rate-limit, sessão expirada, rede), o estado local reverte para o que
 * era antes do clique. O spinner por linha continua indicando "operação em
 * voo" mas o valor já foi trocado.
 *
 * Segurança: se qualquer chamada retornar 403 (usuário perdeu privilégio de
 * admin entre carregamento e ação), redirecionamos para o /app e
 * expomos uma mensagem de erro persistente na navegação. O gate primário
 * é a checagem no ModuleRoute; este é o cinto-de-segurança.
 */

type RowState =
  | { kind: "loading" }
  | { kind: "error"; message: string };

export function FeatureFlagsPage() {
  const nav = useNavigate();
  const { user } = useAuth();
  const [flags, setFlags] = useState<FeatureFlag[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [patchState, setPatchState] = useState<Record<string, RowState>>({});

  // Auto-dismiss do notice após 6s (débito levantado pelo QA1).
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 6000);
    return () => clearTimeout(t);
  }, [notice]);

  const load = useCallback(async () => {
    setError(null);
    try {
      const { flags } = await api.featureFlags.list();
      setFlags(flags);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) {
        // Não deveria acontecer (o roteamento já protege), mas se acontecer
        // manda o usuário embora com aviso.
        nav("/app", { replace: true });
        return;
      }
      setError(
        err instanceof ApiError
          ? `Erro ao carregar flags (${err.status}).`
          : "Erro de rede ao carregar flags.",
      );
    }
  }, [nav]);

  useEffect(() => {
    void load();
  }, [load]);

  async function toggle(flag: FeatureFlag) {
    const key = flag.key;
    const next = !flag.enabled;

    // Snapshot antes da mudança otimista — usado para reverter em erro.
    const previousFlags = flags;

    // OTIMISMO: trocamos o estado local ANTES de aguardar a rede. A UI
    // reflete a nova posição do toggle imediatamente; se der ruim, revertemos.
    // O updatedAt/updatedBy aqui é um palpite local (o backend não devolve
    // updatedAt no PATCH — débito documentado); vai ser sobrescrito
    // pelo response no sucesso.
    setFlags((cur) =>
      cur
        ? cur.map((f) =>
            f.key === key
              ? {
                  ...f,
                  enabled: next,
                  updatedAt: new Date().toISOString(),
                  updatedBy: user?.id ?? f.updatedBy,
                }
              : f,
          )
        : cur,
    );
    setPatchState((prev) => ({ ...prev, [key]: { kind: "loading" } }));
    setNotice(null);

    try {
      const res = await api.featureFlags.patch(key, { enabled: next });
      // Confirma com o valor autoritativo do servidor (backend pode ajustar
      // via cache/validação, mesmo que raro).
      setFlags((cur) =>
        cur
          ? cur.map((f) =>
              f.key === key
                ? {
                    ...f,
                    enabled: res.flag.enabled,
                    value: res.flag.value,
                  }
                : f,
            )
          : cur,
      );
      setPatchState((prev) => {
        const nextState = { ...prev };
        delete nextState[key];
        return nextState;
      });
      setNotice(
        `Flag "${key}" ${res.flag.enabled ? "ativada" : "desativada"}. Pode levar até 60s para propagar no cache.`,
      );
    } catch (err) {
      // Reverte: restaura o snapshot pré-otimismo.
      setFlags(previousFlags);
      if (err instanceof ApiError) {
        if (err.status === 403) {
          nav("/app", { replace: true });
          return;
        }
        if (err.status === 429) {
          setPatchState((prev) => ({
            ...prev,
            [key]: {
              kind: "error",
              message: "Muitas alterações, aguarde 1 minuto.",
            },
          }));
          return;
        }
        setPatchState((prev) => ({
          ...prev,
          [key]: {
            kind: "error",
            message: `Erro ${err.status} ao atualizar a flag.`,
          },
        }));
        return;
      }
      setPatchState((prev) => ({
        ...prev,
        [key]: {
          kind: "error",
          message: "Erro de rede ao atualizar a flag.",
        },
      }));
    }
  }

  return (
    <div>
      <div className="nx-page-head">
        <div className="nx-page-head__title">
          <span className="nx-page-head__eyebrow">Sistema</span>
          <h1>Feature flags</h1>
          <div className="nx-page-head__sub">
            Toggles administrativos. Alterações propagam em até 60 segundos
            devido ao cache do backend.
          </div>
        </div>
      </div>

      {error && (
        <div role="alert" className="nx-error">
          {error}
        </div>
      )}
      {notice && (
        <div
          role="status"
          className="nx-success"
          style={{ marginTop: "var(--nx-space-4)" }}
        >
          {notice}
        </div>
      )}

      <div className="nx-card" style={{ marginTop: "var(--nx-space-4)", padding: 0 }}>
        <div className="nx-table-wrap">
          <table className="nx-table" aria-label="Feature flags">
            <thead>
              <tr>
                <th>Chave</th>
                <th>Estado</th>
                <th>Descrição</th>
                <th>Atualizada em</th>
                <th>Por</th>
              </tr>
            </thead>
            <tbody>
              {flags === null && !error && (
                <tr>
                  <td colSpan={5} style={{ color: "var(--nx-mute)" }}>
                    Carregando…
                  </td>
                </tr>
              )}
              {flags !== null && flags.length === 0 && (
                <tr>
                  <td colSpan={5} style={{ color: "var(--nx-mute)" }}>
                    Nenhuma flag cadastrada.
                  </td>
                </tr>
              )}
              {flags?.map((f) => {
                const rowState = patchState[f.key];
                const rowLoading = rowState?.kind === "loading";
                const rowError =
                  rowState?.kind === "error" ? rowState.message : null;
                return (
                  <tr key={f.key}>
                    <td>
                      <code className="mono">{f.key}</code>
                    </td>
                    <td>
                      <FlagToggle
                        checked={f.enabled}
                        loading={rowLoading}
                        onToggle={() => void toggle(f)}
                        label={
                          f.enabled
                            ? `Desativar ${f.key}`
                            : `Ativar ${f.key}`
                        }
                      />
                      {rowError && (
                        <div
                          role="alert"
                          className="nx-error"
                          style={{ marginTop: 6, fontSize: "var(--nx-text-xs)" }}
                        >
                          {rowError}
                        </div>
                      )}
                    </td>
                    <td style={{ color: "var(--nx-mute-strong)" }}>
                      {f.description || "—"}
                    </td>
                    <td style={{ color: "var(--nx-mute)", whiteSpace: "nowrap" }}>
                      {formatDate(f.updatedAt)}
                    </td>
                    <td style={{ color: "var(--nx-mute)" }}>
                      {f.updatedBy === null ? "—" : `usuário #${f.updatedBy}`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/**
 * Toggle switch acessível (checkbox real por trás). Preserva foco visível,
 * respeita `disabled` durante loading. Sem libraries — CSS puro em
 * global.css controla o visual.
 */
interface FlagToggleProps {
  checked: boolean;
  loading: boolean;
  onToggle: () => void;
  label: string;
}

function FlagToggle({ checked, loading, onToggle, label }: FlagToggleProps) {
  return (
    <label
      className={
        "nx-toggle" +
        (checked ? " nx-toggle--on" : "") +
        (loading ? " nx-toggle--loading" : "")
      }
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={onToggle}
        disabled={loading}
        aria-label={label}
      />
      <span className="nx-toggle__track" aria-hidden="true">
        <span className="nx-toggle__thumb" />
      </span>
      <span className="nx-toggle__text">
        {loading ? "…" : checked ? "Ativa" : "Inativa"}
      </span>
    </label>
  );
}

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("pt-BR");
  } catch {
    return iso;
  }
}
